import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuthenticatedUser, useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

import { talentReviewDaysLeft } from '../talentReviewDate';
import useTalentReviewGridPersistence from './useTalentReviewGridPersistence';

type AnswerPlacementHelp = (
	userID: string,
	answers: graphql.TalentReviewPlacementHelpAnswerInput[],
) => Promise<void>;

/**
 * Everything the talent review page needs besides rendering: the members of
 * the group, the optimistic placements with the actions that persist them, and
 * the cycle moment this grid was opened from.
 *
 * The page component keeps only its own UI state (drag, modals, selection).
 * @param options hook input
 * @param options.grid the currently loaded grid
 * @param options.templateID the template selected in the UI
 * @param options.groupID the group selected in the UI
 * @param options.localDate the day selected in the UI
 * @param options.isSnapshot true on a submitted (frozen) talent review
 * @param options.correcting true while a superuser corrects a snapshot: the grid
 * is editable and changes stay local until saved
 */
export default function useTalentReview(options: {
	grid: graphql.TalentReviewGrid;
	templateID: string;
	groupID: string;
	localDate: graphql.Scalars['LocalDate']['input'];
	isSnapshot?: boolean;
	correcting?: boolean;
}): {
	today: string;
	placements: graphql.TalentReviewGridPlacement[];
	stale: boolean;
	readonly: boolean;
	resetPlacements: () => void;
	users: graphql.User[];
	unplacedUsers: graphql.User[];
	cycleMoment: graphql.TalentReviewCycleMomentFragment | undefined;
	submitted: boolean;
	daysLeft: number | undefined;
	movePlacement: (target: {
		userID: string;
		row: number;
		column: number;
		position?: number;
		renumbered?: ReadonlyMap<string, number>;
		motivation?: string | null;
	}) => void;
	removePlacement: (userID: string) => void;
	clearGrid: () => Promise<void>;
	updatePlacement: (placement: graphql.TalentReviewGridPlacement) => void;
	updatePlacements: (placements: graphql.TalentReviewGridPlacement[]) => void;
	updatePlacementMotivation: (placement: graphql.TalentReviewGridPlacement) => void;
	updateMotivation: (userID: string, motivation: string) => void;
	flushMotivation: (userID: string) => void;
	answerPlacementHelp: AnswerPlacementHelp;
} {
	const { grid, isSnapshot, correcting } = options;
	const history = useHistory();
	const location = useLocation();
	const authenticatedUser = useAuthenticatedUser();
	const hideOwnerInTalentReview = useFeature(graphql.FeatureName.HideOwnerInTalentReview);
	const momentID = new URLSearchParams(location.search).get('moment_id') ?? undefined;

	const today = useMemo(() => moment().format('YYYY-MM-DD'), []);

	const {
		placements,
		stale,
		persist,
		persistMotivation,
		flushMotivation,
		reset,
		clearGrid,
		answerPlacementHelp,
	} = useTalentReviewGridPersistence({
		grid,
		expected: {
			templateID: options.templateID,
			groupID: options.groupID,
			localDate: options.localDate,
		},
		local: correcting,
	});

	const readonly = correcting
		? false
		: !!isSnapshot || !grid.viewerCanUpdate || grid.localDate < today || stale;

	const users = useMemo(() => {
		return grid.group.members.edges
			.filter(({ role }) => role.type !== graphql.RoleType.Owner || !hideOwnerInTalentReview)
			.map(({ node }) => node as graphql.User);
	}, [grid.group.members.edges, hideOwnerInTalentReview]);

	const unplacedUsers = useMemo(() => {
		return users.filter((user) => !placements.some((placement) => placement.user.id === user.id));
	}, [placements, users]);

	const momentQuery = graphql.useMomentForTalentReviewQuery({
		variables: { id: momentID! },
		skip: !momentID || !!isSnapshot,
		errorPolicy: 'all',
	});

	// Without moment_id (sidebar navigation) the server resolves the viewer's
	// cycle moment for the group. Kept active alongside the moment query: it
	// backs the callout for viewers who may not read the moment by id, and
	// submit/revert refetch it.
	const cycleQuery = graphql.useTalentReviewCycleMomentQuery({
		variables: { groupID: grid.group.id },
		skip: !!isSnapshot,
		errorPolicy: 'all',
	});

	const cycleMoment = useMemo(() => {
		const fromURL = momentID ? momentQuery.data?.momentInstance : undefined;
		const fromGroup = cycleQuery.data?.group?.talentReviewCycleMoment ?? undefined;
		const instance = momentID
			? (fromURL ?? (fromGroup?.id === momentID ? fromGroup : undefined))
			: fromGroup;
		if (!instance) {
			return undefined;
		}

		// Open moments belong to their owner; a completed one also shows for
		// group managers and HR, who may revert it until the deadline.
		const canRevertForOther =
			instance.status === graphql.MomentStatus.Completed && instance.talentReviewViewerCanRevert;
		if (instance.user.id !== authenticatedUser.id && !canRevertForOther) {
			return undefined;
		}

		if (instance.momentTemplate.type !== graphql.MomentType.TalentReview) {
			return undefined;
		}

		// Open moments can be submitted; completed ones show as submitted and can
		// be reverted until the deadline.
		if (
			instance.status !== graphql.MomentStatus.Open &&
			instance.status !== graphql.MomentStatus.Completed
		) {
			return undefined;
		}

		// A moment is bound to a group and (usually) a template; submitting from
		// another template's grid would be refused by the server.
		const talentReview = instance.momentTemplate.talentReview;
		if (talentReview?.group?.id && talentReview.group.id !== grid.group.id) {
			return undefined;
		}

		if (talentReview?.template?.id && talentReview.template.id !== grid.template.id) {
			return undefined;
		}

		return instance;
	}, [
		authenticatedUser.id,
		cycleQuery.data?.group?.talentReviewCycleMoment,
		grid.group.id,
		grid.template.id,
		momentID,
		momentQuery.data?.momentInstance,
	]);

	const submitted = cycleMoment?.status === graphql.MomentStatus.Completed;

	const daysLeft = cycleMoment?.endDate ? talentReviewDaysLeft(cycleMoment.endDate) : undefined;

	// A resolved cycle moment goes into the URL, so the rest of the page (day
	// navigation, submit) works exactly as when opened from the cycle task.
	useEffect(() => {
		if (momentID || !cycleMoment) {
			return;
		}

		const search = new URLSearchParams(location.search);
		search.set('moment_id', cycleMoment.id);
		history.replace({
			pathname: location.pathname,
			search: search.toString(),
		});
	}, [cycleMoment, history, location.pathname, location.search, momentID]);

	// A moment_id that does not resolve to a usable moment (wrong user, closed,
	// other group or template) would keep claiming this grid is part of a
	// cycle. Drop it from the URL once the query has settled. Overdue open
	// moments stay.
	useEffect(() => {
		if (!momentID || cycleMoment) {
			return;
		}

		if (momentQuery.loading || !momentQuery.called || momentQuery.error || !momentQuery.data) {
			return;
		}

		const search = new URLSearchParams(location.search);
		search.delete('moment_id');
		history.replace({
			pathname: location.pathname,
			search: search.toString(),
		});
	}, [
		cycleMoment,
		history,
		location.pathname,
		location.search,
		momentID,
		momentQuery.called,
		momentQuery.data,
		momentQuery.error,
		momentQuery.loading,
	]);

	/**
	 * Places a user in a cell, or moves them there from another cell. Cells order
	 * their people by `position` (see talentReviewCellOrder), so the array
	 * position is irrelevant. A move starts a fresh motivation: the old one
	 * explained the old cell.
	 * @param target Where to put whom.
	 * @param target.userID The person to place.
	 * @param target.row Target cell row.
	 * @param target.column Target cell column.
	 * @param target.position Place among the others in the cell; omitted = at the end.
	 * @param target.renumbered New positions for others in the cell, when there was no room between two of them.
	 * @param target.motivation Motivation to keep, e.g. when undoing a move.
	 */
	const movePlacement = (target: {
		userID: string;
		row: number;
		column: number;
		position?: number;
		renumbered?: ReadonlyMap<string, number>;
		motivation?: string | null;
	}) => {
		const user = users.find((item) => item.id === target.userID);
		if (!user) {
			return;
		}

		const current = placements.find((placement) => placement.user.id === target.userID);
		const remaining = placements
			.filter(({ user: item }) => item.id !== target.userID)
			.map((placement) => {
				const position = target.renumbered?.get(placement.user.id);
				return position === undefined ? placement : { ...placement, position };
			});

		// Mirror the server's rule for `previous` (the definitive placement of the
		// last earlier day) so the sidebar card can show "from → to" and undo the
		// move before the response lands. A placement from an earlier day becomes
		// the previous one; one written today already carries it.
		const previous =
			current && current.localDate !== grid.localDate
				? { ...current, previous: undefined }
				: current?.previous;

		const next = remaining.concat({
			user,
			row: target.row,
			column: target.column,
			position: target.position,
			motivation: target.motivation ?? undefined,
			score: current?.score,
			retentionRisk: current?.retentionRisk,
			previous,
			localDate: grid.localDate,
			createdAt: new Date().toISOString(),
			// Optimistic placeholder; the server sets the real actor.
			createdBy: user,
		});
		void persist(next, target.userID);
		target.renumbered?.forEach((_, userID) => {
			void persist(next, userID);
		});
	};

	/**
	 * Saves several placements at once, one write per person, from one
	 * optimistic state. Used when people change place within a cell.
	 * @param updated the changed placements
	 */
	const updatePlacements = (updated: graphql.TalentReviewGridPlacement[]) => {
		const byUser = new Map(updated.map((item) => [item.user.id, item]));
		const next = placements.map((item) => byUser.get(item.user.id) ?? item);
		for (const item of updated) {
			void persist(next, item.user.id);
		}
	};

	const removePlacement = (userID: string) => {
		void persist(
			placements.filter(({ user }) => user.id !== userID),
			userID,
			true,
		);
	};

	const updatePlacement = (placement: graphql.TalentReviewGridPlacement) => {
		void persist(
			placements.map((item) => (item.user.id === placement.user.id ? placement : item)),
			placement.user.id,
		);
	};

	/**
	 * Saves a motivation; debounced per user, so safe to call per keystroke.
	 * @param placement the placement with the updated motivation
	 */
	const updatePlacementMotivation = (placement: graphql.TalentReviewGridPlacement) => {
		persistMotivation(
			placements.map((item) => (item.user.id === placement.user.id ? placement : item)),
			placement.user.id,
		);
	};

	const updateMotivation = (userID: string, motivation: string) => {
		const placement = placements.find((item) => item.user.id === userID);
		if (!placement) {
			return;
		}

		updatePlacementMotivation({ ...placement, motivation });
	};

	return {
		today,
		placements,
		stale,
		// A submitted review is frozen; revert it (see TalentReviewV2) to edit again.
		readonly: readonly || (submitted && !correcting),
		resetPlacements: reset,
		users,
		unplacedUsers,
		cycleMoment,
		submitted,
		daysLeft,
		movePlacement,
		removePlacement,
		clearGrid,
		updatePlacement,
		updatePlacements,
		updatePlacementMotivation,
		updateMotivation,
		flushMotivation,
		answerPlacementHelp,
	};
}
